'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';

export default function DevotionalsPage() {
  const [devotionals, setDevotionals] = useState([]);
  const [filteredDevotionals, setFilteredDevotionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  
  // Metadata
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    loadDevotionals();
    loadMetadata();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [devotionals, searchTerm, selectedCategory, selectedTag]);

  const loadDevotionals = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('🔍 Fetching devotionals from API...');
      
      const response = await fetch('/api/devotionals');
      
      if (!response?.ok) {
        const errData = await response?.json()?.catch(() => ({}));
        throw new Error(errData?.error || `Request failed with status ${response.status}`);
      }

      const { data } = await response?.json();

      console.log('📊 API response:', { count: data?.length });

      if (!data || data?.length === 0) {
        console.warn('⚠️ No published devotionals found in database');
        setError('No devotionals available yet. Please check back later or contact the administrator.');
        setDevotionals([]);
        return;
      }

      console.log(`✅ Successfully loaded ${data?.length} devotionals`);

      const formattedData = data?.map((item) => ({
        id: item?.id,
        slug: item?.slug,
        title: item?.title,
        subtitle: item?.subtitle,
        excerpt: item?.excerpt,
        coverImageUrl: item?.cover_image_url,
        coverImageAlt: item?.cover_image_alt,
        category: item?.category,
        tags: item?.tags || [],
        publishedAt: item?.published_at
      }));

      setDevotionals(formattedData);
    } catch (err) {
      console.error('❌ Error loading devotionals:', err);
      setError(err?.message || 'Failed to load devotionals. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const loadMetadata = async () => {
    try {
      const response = await fetch('/api/devotionals');
      if (!response?.ok) return;

      const { data } = await response?.json();
      if (!data) return;

      const uniqueCategories = [...new Set(data.map((item) => item?.category).filter(Boolean))];
      setCategories(uniqueCategories?.sort());

      const allTags = data?.flatMap((item) => item?.tags || [])?.filter(Boolean);
      const uniqueTags = [...new Set(allTags)];
      setTags(uniqueTags?.sort());
    } catch (err) {
      console.error('Error loading metadata:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...devotionals];

    if (searchTerm) {
      const term = searchTerm?.toLowerCase();
      filtered = filtered?.filter(
        (dev) =>
          dev?.title?.toLowerCase()?.includes(term) ||
          dev?.excerpt?.toLowerCase()?.includes(term)
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered?.filter((dev) => dev?.category === selectedCategory);
    }

    if (selectedTag !== 'all') {
      filtered = filtered?.filter((dev) => dev?.tags?.includes(selectedTag));
    }

    setFilteredDevotionals(filtered);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Daily Devotionals
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Discover spiritual insights and daily encouragement
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <input
              type="text"
              placeholder="Search devotionals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Categories</option>
              {categories?.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            {/* Tag Filter */}
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Tags</option>
              {tags?.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Content */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <p className="font-semibold mb-2">Unable to load devotionals</p>
            <p>{error}</p>
            <button
              onClick={loadDevotionals}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading devotionals...</p>
          </div>
        ) : filteredDevotionals?.length === 0 && !error ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-gray-600 dark:text-gray-400 text-lg mt-4">
              {devotionals?.length === 0
                ? 'No devotionals have been published yet.' :'No devotionals found matching your filters. Try adjusting your search.'}
            </p>
            {devotionals?.length === 0 && (
              <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
                Devotionals added in the admin panel must be set to "Published" status to appear here.
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDevotionals?.map((devotional) => (
              <Link
                key={devotional?.id}
                href={`/devotionals/${devotional?.slug}`}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                {devotional?.coverImageUrl && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={devotional?.coverImageUrl}
                      alt={devotional?.coverImageAlt || devotional?.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = '/assets/images/no_image.png';
                      }}
                    />
                  </div>
                )}
                
                <div className="p-6">
                  {devotional?.category && (
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs font-semibold rounded-full mb-2">
                      {devotional?.category}
                    </span>
                  )}
                  
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {devotional?.title}
                  </h2>
                  
                  {devotional?.subtitle && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {devotional?.subtitle}
                    </p>
                  )}
                  
                  {devotional?.excerpt && (
                    <p className="text-gray-700 dark:text-gray-300 mb-4 line-clamp-3">
                      {devotional?.excerpt}
                    </p>
                  )}
                  
                  {devotional?.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {devotional?.tags?.slice(0, 3)?.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 text-xs rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(devotional?.publishedAt)?.toLocaleDateString()}
                    </span>
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">
                      Read more →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}